import {
    getSensorsAccessoriesPageConfig,
    SensorsAccessoriesPageData,
    SensorsAccessoriesPageVariables,
} from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { Suspense, useContext, useMemo } from 'react';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { ConfigContext } from 'src/configContext.ts';
import { buildCheckoutVariables } from 'src/sensors/checkout/buildCheckoutVariables.ts';
import { LocationState } from 'src/sensors/LocationState.ts';
import { getFilterValuesFromUrl } from 'src/utils/getFilterValuesFromUrl.ts';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';

type FetchSensorSelectionProps = {
    searchParams: string;
    locationState: LocationState;
}

export const FetchSensorSelection = ({ searchParams, locationState }: FetchSensorSelectionProps) => {
    const {
        sensorLabel,
    } = locationState;

    if (!sensorLabel) {
        throw new Error('No Sensor selected');
    }

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getSensorsAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const filter = getFilterValuesFromUrl(new URLSearchParams(searchParams), pageConfig.filterConfig);

    const variables = useMemo(() => buildCheckoutVariables({
        mandatoryFilterConfig,
        filter,
        sensorLabel,
        userSettings,
    }), [
        mandatoryFilterConfig,
        filter,
        sensorLabel,
        userSettings,
    ]);

    return (
        <Suspense fallback={<SkeletonSelection/>}>
            <Query<SensorsAccessoriesPageData, SensorsAccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return 'Loading...';
                    }
                    const { sensor } = data.accessories;
                    const accessories = prepareAccessories(
                        data.accessories.accessories,
                        locationState.selectedAccessories,
                        userSettings,
                        null,
                        (a, b) => sortAccessories(a.accessory, b.accessory),
                    );

                    return (
                        <>
                            <ProjectSelectionDeviceList
                                title={<I18n messageId={MESSAGES.checkoutSensorsTitle}/>}
                            >
                                <ProjectSelectionDevice
                                    productImage={(
                                        <ProductImage
                                            imgSrc={sensor.image?.raw.join('') || ''}
                                        >
                                            <ValveIcon/>
                                        </ProductImage>
                                    )}
                                    productLink={(
                                        <ViewProductLink
                                            userSettings={userSettings}
                                            code={sensor.Label}
                                            content=""
                                        >
                                            <span className="vss-device__product-label">
                                                {sensor.Label}
                                            </span>

                                            <div className="vss-device__product-icon">
                                                <ArrowRightIcon/>
                                            </div>
                                        </ViewProductLink>
                                    )}
                                    productDescription={(
                                        <RenderHtml
                                            data={sensor.productType?.localized.join('') || ''}
                                        />
                                    )}
                                    quantity={locationState.qty}
                                />
                            </ProjectSelectionDeviceList>

                            {accessories.length ? (
                                <ProjectSelectionDeviceList
                                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                                >
                                    {accessories.map(({ qty, accessory: { item } }) => (
                                        <ProjectSelectionDevice
                                            key={item.Label}
                                            productImage={(
                                                <ProductImage
                                                    imgSrc={item.image?.raw.join('') || ''}
                                                >
                                                    <ActuatorIcon/>
                                                </ProductImage>
                                            )}
                                            productLink={(
                                                <ViewProductLink
                                                    userSettings={userSettings}
                                                    code={item.Label}
                                                    content=""
                                                >
                                                    {item.Label}

                                                    <div className="vss-accessory__product-icon">
                                                        <ArrowRightIcon/>
                                                    </div>
                                                </ViewProductLink>
                                            )}
                                            productDescription={item.accessoryType?.localized.join('') || ''}
                                            quantity={qty}
                                        />
                                    ))}

                                </ProjectSelectionDeviceList>
                            ) : null}
                        </>
                    );
                }}
            </Query>
        </Suspense>
    );
};
